import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react';
import { api } from './api';

/**
 * Fetches `path` and keeps the result.
 *
 * A reload keeps the current data on screen and sets `refreshing`; only a change
 * of `path` clears it and shows `loading`. Clearing on every reload used to swap
 * whole panels for a spinner after each save, and unmounted inputs mid-typing
 * wherever a page polled. A superseded request is aborted, not just ignored.
 */
export type UseApiOptions = {
  /**
   * Opt in when every path this hook is given returns the same shape (one
   * endpoint, different query string, such as a date range). On a path change
   * the previous result stays on screen with `refreshing` set, instead of
   * blanking to `loading`, until the new result arrives.
   */
  keepPrevious?: boolean;
};

export function useApi<T>(path: string | null, options: UseApiOptions = {}) {
  const keepPrevious = Boolean(options.keepPrevious);
  // The result remembers which path it came from. On the render after `path`
  // changes, before the effect has cleared anything, the old result used to be
  // returned for the new path: Traction's Vision object reached a table and
  // crashed it ("rows.slice is not a function"). A result for another path is
  // never returned; that render reads as loading instead.
  const [result, setResult] = useState<{ path: string | null; value: T | null }>({ path: null, value: null });
  const [loading, setLoading] = useState(Boolean(path));
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision(value => value + 1), []);
  const shownPath = useRef<string | null>(null);
  const setData = useCallback((next: SetStateAction<T | null>) => setResult(current => ({
    path: current.path,
    value: typeof next === 'function' ? (next as (previous: T | null) => T | null)(current.value) : next,
  })), []);

  useEffect(() => {
    if (!path) { shownPath.current = null; setResult({ path: null, value: null }); setError(null); setLoading(false); setRefreshing(false); return; }
    const controller = new AbortController();
    const samePath = shownPath.current === path;
    const keepShown = keepPrevious && shownPath.current !== null;
    if (samePath || keepShown) setRefreshing(true);
    else { setResult({ path, value: null }); setLoading(true); }
    setError(null);
    api<T>(path, { signal: controller.signal })
      .then(value => {
        if (controller.signal.aborted) return;
        shownPath.current = path;
        setResult({ path, value });
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        setError(reason instanceof Error ? reason.message : 'Request failed');
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        setRefreshing(false);
      });
    return () => controller.abort();
  }, [path, revision, keepPrevious]);

  const current = result.path === path;
  // keepPrevious: the last result (for another query of the same endpoint) stays
  // up while the new one loads. Default callers never see another path's result.
  const stale = !current && keepPrevious && result.value !== null && !error;
  return {
    data: current || stale ? result.value : null,
    loading: stale ? false : loading || (!current && !error),
    refreshing: refreshing || stale,
    error,
    reload,
    setData,
  };
}
